import {
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

// Navigable rows and cells carry these attributes; group header bands, skeleton rows and drop zones don't.
const ROW_SELECTOR = 'tr[data-grid-row]';
const CELL_SELECTOR = '[data-grid-cell]';
// Holds a control's own tabindex (empty when it had none) while the grid keeps it out of the Tab order.
const SAVED_TABINDEX = 'data-grid-tabindex';
const HEADER_ROW_KEY = 'header';
const FALLBACK_PAGE_ROWS = 10;

type Position = { rowKey: string; columnKey: string; rowIndex: number; columnIndex: number };

type UseGridNavigationOptions = {
  enabled: boolean;
  gridRef: RefObject<HTMLTableElement | null>;
  /** Scroll container whose height sets how far Page Up and Page Down move. */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Id of the keyboard instructions, read when Tab brings focus into the grid. */
  instructionsId: string;
  /** Toggles the selection of a body row; Space does nothing on rows when omitted. */
  onToggleRowSelection?: (rowId: string) => void;
  /** Skips the Tab-order upkeep while set, such as during a column resize or drag; it runs again once cleared. */
  paused?: boolean;
};

function gridRows(grid: HTMLTableElement): HTMLElement[] {
  return Array.from(grid.querySelectorAll<HTMLElement>(ROW_SELECTOR)).filter((row) => row.closest('table') === grid);
}

function rowCells(row: HTMLElement): HTMLElement[] {
  return Array.from(row.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.matches(CELL_SELECTOR),
  );
}

function rowKey(row: HTMLElement): string {
  return row.dataset.rowId ?? HEADER_ROW_KEY;
}

// Every focusable element in the cell, including those the grid already took out of the Tab order.
function cellControls(cell: HTMLElement): HTMLElement[] {
  const controls: HTMLElement[] = [];
  const walker = document.createTreeWalker(cell, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const el = node as HTMLElement & { disabled?: boolean };
      if (el.hasAttribute(SAVED_TABINDEX)) return NodeFilter.FILTER_ACCEPT;
      return el.tabIndex >= 0 && !el.disabled ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  while (walker.nextNode()) controls.push(walker.currentNode as HTMLElement);
  return controls;
}

function isShown(el: HTMLElement, cell: HTMLElement): boolean {
  for (let node: HTMLElement | null = el; node && node !== cell; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return !(el as HTMLElement & { disabled?: boolean }).disabled;
}

function removeFromTabOrder(el: HTMLElement) {
  if (!el.hasAttribute(SAVED_TABINDEX)) el.setAttribute(SAVED_TABINDEX, el.getAttribute('tabindex') ?? '');
  if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
}

function restoreTabOrder(el: HTMLElement) {
  const saved = el.getAttribute(SAVED_TABINDEX);
  if (saved === null) return;
  if (saved === '') el.removeAttribute('tabindex');
  else el.setAttribute('tabindex', saved);
  el.removeAttribute(SAVED_TABINDEX);
}

// Keys inside these belong to the control, so the grid leaves them alone.
function ownsArrowKeys(el: Element): boolean {
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(el.type);
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  const role = el.getAttribute('role');
  return role !== null && ['combobox', 'listbox', 'slider', 'spinbutton', 'textbox', 'menu', 'tree'].includes(role);
}

// Runs a control's default action: a native click, or Enter for a role="button" element such as a drag handle.
function activate(control: HTMLElement) {
  if (
    control instanceof HTMLButtonElement ||
    control instanceof HTMLAnchorElement ||
    control instanceof HTMLInputElement
  ) {
    control.click();
    return;
  }
  control.dispatchEvent(new globalThis.KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
}

/**
 * Data grid keyboard model (WAI-ARIA grid pattern): the grid is one Tab stop, arrow keys move between cells,
 * and Enter or F2 reach the controls inside a cell. Tab and Shift+Tab are never intercepted.
 */
export function useGridNavigation({
  enabled,
  gridRef,
  scrollContainerRef,
  instructionsId,
  onToggleRowSelection,
  paused = false,
}: UseGridNavigationOptions) {
  const positionRef = useRef<Position | null>(null);
  // Cell whose controls are back in the Tab order after Enter or F2.
  const interactingCellRef = useRef<HTMLElement | null>(null);
  // Set while activate() dispatches Enter, so the grid doesn't handle its own event.
  const activatingRef = useRef(false);
  // Cells given a tabindex, so turning the grid off can clear them after they lose their grid attributes.
  const managedCellsRef = useRef(new Set<HTMLElement>());

  const findActiveCell = useCallback((rows: HTMLElement[]): HTMLElement | undefined => {
    const position = positionRef.current;
    if (rows.length === 0) return undefined;
    if (!position) return rowCells(rows[0])[0];
    const rowIndex = rows.findIndex((row) => rowKey(row) === position.rowKey);
    const row = rows[rowIndex >= 0 ? rowIndex : Math.min(position.rowIndex, rows.length - 1)];
    const cells = rowCells(row);
    return (
      cells.find((cell) => cell.dataset.columnId === position.columnKey) ??
      cells[Math.min(position.columnIndex, cells.length - 1)]
    );
  }, []);

  const remember = useCallback((cell: HTMLElement, rows: HTMLElement[]) => {
    const row = cell.parentElement as HTMLElement;
    positionRef.current = {
      rowKey: rowKey(row),
      columnKey: cell.dataset.columnId ?? '',
      rowIndex: rows.indexOf(row),
      columnIndex: rowCells(row).indexOf(cell),
    };
  }, []);

  // Makes `active` the grid's one Tab stop. `described` hangs the instructions on it, so they are read when Tab
  // brings focus in and dropped on the first move inside the grid.
  const setTabStop = useCallback(
    (cells: HTMLElement[], active: HTMLElement | undefined, described: boolean) => {
      for (const cell of cells) {
        managedCellsRef.current.add(cell);
        const tabIndex = cell === active ? '0' : '-1';
        if (cell.getAttribute('tabindex') !== tabIndex) cell.setAttribute('tabindex', tabIndex);
        if (cell === active && described) cell.setAttribute('aria-describedby', instructionsId);
        else if (cell.getAttribute('aria-describedby') === instructionsId) cell.removeAttribute('aria-describedby');
      }
    },
    [instructionsId],
  );

  // Keeps exactly one cell in the Tab order and every control inside cells out of it.
  const sync = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const rows = gridRows(grid);
    const cells = rows.flatMap(rowCells);

    if (!enabled) {
      for (const el of grid.querySelectorAll<HTMLElement>(`[${SAVED_TABINDEX}]`)) restoreTabOrder(el);
      for (const cell of managedCellsRef.current) {
        cell.removeAttribute('tabindex');
        if (cell.getAttribute('aria-describedby') === instructionsId) cell.removeAttribute('aria-describedby');
      }
      managedCellsRef.current.clear();
      return;
    }

    const interacting = interactingCellRef.current;
    if (interacting && !interacting.isConnected) interactingCellRef.current = null;
    for (const cell of managedCellsRef.current) if (!cell.isConnected) managedCellsRef.current.delete(cell);
    const active = findActiveCell(rows);
    if (active) remember(active, rows);
    // Inside the grid, keep whatever the last focus change decided.
    const focusInside = grid.contains(document.activeElement);
    setTabStop(cells, active, focusInside ? active?.getAttribute('aria-describedby') === instructionsId : true);

    for (const cell of cells) {
      if (cell === interactingCellRef.current) continue;
      for (const control of cellControls(cell)) removeFromTabOrder(control);
    }
  }, [enabled, gridRef, instructionsId, findActiveCell, remember, setTabStop]);

  useLayoutEffect(() => {
    if (!paused) sync();
  });

  // Cells also re-render on their own, without the table.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid || !enabled || paused) return;
    const observer = new MutationObserver(sync);
    observer.observe(grid, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled, paused, gridRef, sync]);

  const stopInteracting = useCallback(() => {
    const cell = interactingCellRef.current;
    interactingCellRef.current = null;
    if (cell) for (const control of cellControls(cell)) removeFromTabOrder(control);
  }, []);

  const focusCell = useCallback(
    (cell: HTMLElement) => {
      const grid = gridRef.current;
      if (!grid) return;
      const rows = gridRows(grid);
      stopInteracting();
      remember(cell, rows);
      setTabStop(rows.flatMap(rowCells), cell, false);
      cell.focus();
    },
    [gridRef, remember, setTabStop, stopInteracting],
  );

  const startInteracting = useCallback((cell: HTMLElement, controls: HTMLElement[]) => {
    interactingCellRef.current = cell;
    for (const control of cellControls(cell)) restoreTabOrder(control);
    controls[0]?.focus();
  }, []);

  const pageRows = useCallback(
    (cell: HTMLElement) => {
      const height = scrollContainerRef.current?.clientHeight ?? 0;
      const rowHeight = cell.offsetHeight;
      return height > 0 && rowHeight > 0 ? Math.max(1, Math.floor(height / rowHeight) - 1) : FALLBACK_PAGE_ROWS;
    },
    [scrollContainerRef],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const grid = gridRef.current;
      if (!enabled || !grid || activatingRef.current) return;
      if (event.defaultPrevented || event.nativeEvent.isComposing || event.altKey || event.metaKey) return;
      const target = event.target as HTMLElement;
      const cell = target.closest<HTMLElement>(CELL_SELECTOR);
      if (!cell || cell.closest('table') !== grid) return;

      // Escape from any control in a cell, including one a closing popover refocused, goes back to the cell.
      if (target !== cell && event.key === 'Escape' && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        focusCell(cell);
        return;
      }
      if (cell === interactingCellRef.current) return;
      // Focus reached a control by pointer; keys that operate it stay with it.
      if (target !== cell && ownsArrowKeys(target)) return;

      const rows = gridRows(grid);
      const row = cell.parentElement as HTMLElement;
      const rowIndex = rows.indexOf(row);
      const columnIndex = rowCells(row).indexOf(cell);
      const lastRow = rows.length - 1;
      const cellAt = (r: number, c: number) => {
        const cells = rowCells(rows[Math.max(0, Math.min(r, lastRow))]);
        return cells[Math.max(0, Math.min(c, cells.length - 1))];
      };

      let next: HTMLElement | undefined;
      if (event.ctrlKey) {
        if (event.shiftKey) return;
        if (event.key === 'Home') next = cellAt(0, 0);
        else if (event.key === 'End') next = cellAt(lastRow, Number.MAX_SAFE_INTEGER);
        else return;
      } else if (event.shiftKey) {
        return;
      } else {
        switch (event.key) {
          case 'ArrowRight':
            next = cellAt(rowIndex, columnIndex + 1);
            break;
          case 'ArrowLeft':
            next = cellAt(rowIndex, columnIndex - 1);
            break;
          case 'ArrowDown':
            next = cellAt(rowIndex + 1, columnIndex);
            break;
          case 'ArrowUp':
            next = cellAt(rowIndex - 1, columnIndex);
            break;
          case 'Home':
            next = cellAt(rowIndex, 0);
            break;
          case 'End':
            next = cellAt(rowIndex, Number.MAX_SAFE_INTEGER);
            break;
          case 'PageDown':
            next = cellAt(rowIndex + pageRows(cell), columnIndex);
            break;
          case 'PageUp':
            next = cellAt(rowIndex - pageRows(cell), columnIndex);
            break;
          case 'Enter':
          case 'F2': {
            if (target !== cell) return;
            const controls = cellControls(cell).filter((control) => isShown(control, cell));
            if (controls.length === 0) return;
            event.preventDefault();
            // Enter runs a cell's only control; a cell with several, such as a header's sort and menu, is entered.
            const single = controls.length === 1 && !ownsArrowKeys(controls[0]) ? controls[0] : undefined;
            const action = event.key === 'Enter' ? single : undefined;
            if (action) {
              activatingRef.current = true;
              try {
                activate(action);
              } finally {
                activatingRef.current = false;
              }
            } else {
              startInteracting(cell, controls);
            }
            return;
          }
          case ' ': {
            const rowId = row.dataset.rowId;
            if (target !== cell || rowId === undefined || !onToggleRowSelection) return;
            event.preventDefault();
            onToggleRowSelection(rowId);
            return;
          }
          default:
            return;
        }
      }

      event.preventDefault();
      if (next) focusCell(next);
    },
    [enabled, gridRef, focusCell, onToggleRowSelection, pageRows, startInteracting],
  );

  // A click or programmatic focus anywhere in a cell makes it the active cell.
  const onFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const grid = gridRef.current;
      if (!enabled || !grid) return;
      const cell = (event.target as HTMLElement).closest<HTMLElement>(CELL_SELECTOR);
      if (!cell || cell.closest('table') !== grid) return;
      if (interactingCellRef.current && interactingCellRef.current !== cell) stopInteracting();
      const rows = gridRows(grid);
      remember(cell, rows);
      const fromOutside = !grid.contains(event.relatedTarget as Node | null);
      setTabStop(rows.flatMap(rowCells), cell, fromOutside && event.target === cell);
    },
    [enabled, gridRef, remember, setTabStop, stopInteracting],
  );

  // Leaving the interacting cell puts its controls back out of the Tab order; leaving the grid re-arms the instructions.
  const onBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const cell = interactingCellRef.current;
      const next = event.relatedTarget as Node | null;
      if (cell && !cell.contains(next)) stopInteracting();
      const grid = gridRef.current;
      if (enabled && grid && !grid.contains(next)) {
        const rows = gridRows(grid);
        setTabStop(rows.flatMap(rowCells), findActiveCell(rows), true);
      }
    },
    [enabled, gridRef, findActiveCell, setTabStop, stopInteracting],
  );

  return { onKeyDown, onFocus, onBlur };
}
